import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { ProductCard } from "@/components/shop/ProductCard";

const MAKERS = {
  grammer: {
    name: "Grammer",
    lt: {
      title: "Grammer sėdynės traktoriams ir technikai",
      desc: "Originalios Grammer sėdynės: Maximo, Actimo, MSG serijos traktoriams, kombainams ir krautuvams. Pneumatinė ir mechaninė pakaba, šildymas, pristatymas per 1–3 d. d.",
      body: [
        "Grammer – vokiečių gamintojas, kurio vairuotojo sėdynės montuojamos gamykloje į John Deere, Claas, Fendt, Case IH ir kitus traktorius. Sėdynės.lt siūlo originalias Grammer traktorių sėdynes ir sėdynes kitai technikai.",
        "Populiariausios serijos – Grammer Maximo (Comfort, Dynamic, Evolution) ir Actimo. Pneumatinė pakaba su 12 V kompresoriumi, juosmens atrama, reguliuojami porankiai ir šildymas – komfortas visai darbo pamainai. Sandėlyje esančias Grammer sėdynes pristatome per 1–3 darbo dienas visoje Lietuvoje ir ES.",
      ],
    },
    en: {
      title: "Grammer seats for tractors and machinery",
      desc: "Genuine Grammer seats: Maximo, Actimo and MSG series for tractors, combines and loaders. Air and mechanical suspension, heating, delivery in 1–3 business days.",
      body: ["Grammer is the German maker whose driver seats are factory-fitted in John Deere, Claas, Fendt, Case IH and other tractors.",
        "The most popular series are Grammer Maximo (Comfort, Dynamic, Evolution) and Actimo, with 12 V air suspension, lumbar support, adjustable armrests and heating."],
    },
  },
  proboss: {
    name: "Proboss",
    lt: {
      title: "Proboss sėdynės traktoriams",
      desc: "Proboss traktorių sėdynės su montavimo komplektu pagal traktoriaus markę: John Deere, Claas, Fendt, Case IH, New Holland, MF, Valtra, Belarus/MTZ.",
      body: [
        "Proboss – kokybiškos ir prieinamos kainos traktorių sėdynės su pneumatine pakaba ir 12 V kompresoriumi. Kiekvieną Proboss sėdynę parenkame pagal jūsų traktoriaus markę ir modelį, kartu su tinkamu montavimo komplektu.",
        "Turime Proboss sėdynių John Deere, Claas, Fendt, Case IH, Steyr, New Holland, Deutz Fahr, Massey Ferguson, Valtra ir Belarus/MTZ traktoriams. Nežinote, kuri traktoriaus sėdynė tinka? Atsiųskite lipduko nuotrauką – parinksime už jus.",
      ],
    },
    en: {
      title: "Proboss tractor seats",
      desc: "Proboss tractor seats with a mounting kit matched to your tractor brand: John Deere, Claas, Fendt, Case IH, New Holland, MF, Valtra, Belarus/MTZ.",
      body: ["Proboss makes quality, affordable tractor seats with air suspension and a 12 V compressor. We match every Proboss seat to your tractor brand and model with the right mounting kit."],
    },
  },
  "total-source": {
    name: "Total Source",
    lt: {
      title: "Total Source sėdynės technikai",
      desc: "Total Source sėdynės traktoriams, ekskavatoriams, krautuvams ir kitai technikai. Pneumatinės sėdynės, pristatymas Lietuvoje ir ES.",
      body: [
        "Total Source – universalios sėdynės traktoriams, ekskavatoriams, krautuvams, šakiniams krautuvams ir kitai žemės ūkio bei statybinei technikai.",
        "Visos Total Source sėdynės – su pneumatine pakaba. Jei neradote reikiamos sėdynės, parašykite mums – parinksime pagal jūsų techniką.",
      ],
    },
    en: {
      title: "Total Source seats for machinery",
      desc: "Total Source seats for tractors, excavators, loaders and other machinery. Air suspension, delivery in Lithuania and the EU.",
      body: ["Total Source offers universal seats for tractors, excavators, loaders, forklifts and other agricultural and construction machinery."],
    },
  },
};

const txt = (m, lang) => m[lang === "lt" ? "lt" : "en"];

const MakerIndex = () => {
  const { lang } = useLang();
  const lt = lang === "lt";
  const title = lt ? "Sėdynės: Grammer, Proboss, Total Source" : "Seats: Grammer, Proboss, Total Source";
  useSeo({ title, description: lt ? "Traktorių sėdynės ir sėdynės technikai pagal gamintoją – Grammer, Proboss, Total Source. Pristatymas per 1–3 d. d." : "Tractor and machinery seats by maker – Grammer, Proboss, Total Source." });
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <h1 data-testid="makers-index-title" className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">{title}</h1>
      <div className="grid md:grid-cols-3 gap-3 mt-10">
        {Object.entries(MAKERS).map(([slug, m]) => (
          <Link key={slug} to={`/sedynes/${slug}`} data-testid={`maker-link-${slug}`}
            className="group border border-[#3B4157] bg-[#292D3C] p-6 hover:border-orange-500 transition-colors">
            <div className="font-display font-black uppercase text-2xl group-hover:text-orange-500 transition-colors">{m.name}</div>
            <p className="text-sm text-slate-400 mt-2">{txt(m, lang).desc}</p>
            <ArrowRight className="w-4 h-4 mt-4 text-slate-600 group-hover:text-orange-500 transition-transform group-hover:translate-x-1" />
          </Link>
        ))}
      </div>
    </div>
  );
};

const MakerPage = ({ m }) => {
  const { t, lang } = useLang();
  const c = txt(m, lang);
  const [items, setItems] = useState(null);
  useSeo({ title: c.title, description: c.desc });
  useEffect(() => { setItems(null); api.get("/products", { params: { brand: m.name } }).then((r) => setItems(r.data)); }, [m.name]);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <nav className="text-xs font-mono-x text-slate-500 mb-6"><Link to="/sedynes" className="hover:text-orange-500">{lang === "lt" ? "Sėdynės" : "Seats"}</Link> / {m.name}</nav>
      <h1 data-testid="maker-page-title" className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight">{c.title}</h1>
      <div data-testid="maker-page-intro" className="max-w-3xl mt-4 space-y-3 text-slate-400">{c.body.map((p) => <p key={p}>{p}</p>)}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {items?.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
      </div>
      {items && !items.length && <p className="text-slate-400 py-16 text-center">{t("no_results")}</p>}
      <div className="mt-12 border-t border-[#3B4157] pt-8 text-sm text-slate-400 flex flex-wrap gap-x-6 gap-y-2">
        <Link to="/traktoriu-sedynes" className="text-orange-500 underline underline-offset-2">{t("bp_index_title")}</Link>
        <Link to="/seat-finder" className="text-orange-500 underline underline-offset-2">{t("nav_finder")}</Link>
      </div>
    </div>
  );
};

export default function SeatMakers() {
  const { slug } = useParams();
  const m = MAKERS[slug];
  return m ? <MakerPage m={m} /> : <MakerIndex />;
}
